/**
 * Data Provider
 *
 * Handles all CRUD operations for React Admin.
 * Communicates with your REST API and transforms data to React Admin format.
 *
 * DEMO MODE:
 * - Set DEMO_MODE to true to use mock data without a backend
 * - Set to false when connecting to a real API
 *
 * CUSTOMIZATION:
 * 1. Update `resourceEndpoints` to map your resources to API endpoints
 * 2. Modify `resourceIdFields` if your resources use different ID field names
 * 3. Add custom methods at the bottom for non-standard operations
 */

import { DataProvider } from 'react-admin';
import queryString from 'query-string';
import { httpClient } from '@/utils/httpClient';
import type {
  PaginatedResponse,
  ReactAdminSort,
  ReactAdminPagination,
  ReactAdminFilter,
} from '@/types/api';

// ============================================
// DEMO MODE CONFIGURATION
// ============================================

/**
 * Enable demo mode for testing without a backend
 * Set to false when connecting to a real API
 */
const DEMO_MODE = false;

/**
 * Demo data for the examples resource
 */
let demoExamples = [
  {
    id: '1',
    name: 'First Example',
    description: 'This is the first example item in demo mode',
    status: 'active',
    createdAt: '2024-01-15T10:30:00Z',
    updatedAt: '2024-01-15T10:30:00Z',
  },
  {
    id: '2',
    name: 'Second Example',
    description: 'Another example to demonstrate the list view',
    status: 'active',
    createdAt: '2024-01-14T14:20:00Z',
    updatedAt: '2024-01-14T14:20:00Z',
  },
  {
    id: '3',
    name: 'Inactive Item',
    description: 'This example is marked as inactive',
    status: 'inactive',
    createdAt: '2024-01-13T09:15:00Z',
    updatedAt: '2024-01-13T09:15:00Z',
  },
  {
    id: '4',
    name: 'Project Alpha',
    description: 'A demo project showing the template capabilities',
    status: 'active',
    createdAt: '2024-01-12T16:45:00Z',
    updatedAt: '2024-01-12T16:45:00Z',
  },
  {
    id: '5',
    name: 'Test Entry',
    description: 'Used for testing purposes',
    status: 'inactive',
    createdAt: '2024-01-11T11:00:00Z',
    updatedAt: '2024-01-11T11:00:00Z',
  },
];

/**
 * Demo data for the categories resource
 */
let demoCategories = [
  { id: '1', name: 'Electronics', description: 'Electronic devices and accessories', status: 'active', createdAt: '2024-01-15T10:00:00Z', updatedAt: '2024-01-15T10:00:00Z' },
  { id: '2', name: 'Furniture', description: 'Office and home furniture', status: 'active', createdAt: '2024-01-14T09:00:00Z', updatedAt: '2024-01-14T09:00:00Z' },
  { id: '3', name: 'Clothing', description: 'Apparel and accessories', status: 'inactive', createdAt: '2024-01-13T08:00:00Z', updatedAt: '2024-01-13T08:00:00Z' },
  { id: '4', name: 'Books', description: 'Books and educational materials', status: 'active', createdAt: '2024-01-12T07:00:00Z', updatedAt: '2024-01-12T07:00:00Z' },
  { id: '5', name: 'Sports', description: 'Sports equipment and gear', status: 'active', createdAt: '2024-01-11T06:00:00Z', updatedAt: '2024-01-11T06:00:00Z' },
];

/**
 * Demo data for the tags resource
 */
let demoTags = [
  { id: '1', name: 'Featured', color: '#0093B2', status: 'active', createdAt: '2024-01-15T10:00:00Z', updatedAt: '2024-01-15T10:00:00Z' },
  { id: '2', name: 'New', color: '#4CAF50', status: 'active', createdAt: '2024-01-14T09:00:00Z', updatedAt: '2024-01-14T09:00:00Z' },
  { id: '3', name: 'Sale', color: '#F44336', status: 'active', createdAt: '2024-01-13T08:00:00Z', updatedAt: '2024-01-13T08:00:00Z' },
  { id: '4', name: 'Archived', color: '#9E9E9E', status: 'inactive', createdAt: '2024-01-12T07:00:00Z', updatedAt: '2024-01-12T07:00:00Z' },
  { id: '5', name: 'Premium', color: '#FF9800', status: 'active', createdAt: '2024-01-11T06:00:00Z', updatedAt: '2024-01-11T06:00:00Z' },
];

/**
 * Registry of all demo data arrays by resource name
 */
const demoDataRegistry: Record<string, () => any[]> = {
  examples: () => demoExamples,
  categories: () => demoCategories,
  tags: () => demoTags,
};

const setDemoData = (resource: string, data: any[]) => {
  if (resource === 'examples') demoExamples = data;
  else if (resource === 'categories') demoCategories = data;
  else if (resource === 'tags') demoTags = data;
};

// ============================================
// RESOURCE CONFIGURATION
// ============================================

/**
 * Map resource names to API endpoints
 *
 * CUSTOMIZE: Add your resources here
 */
const resourceEndpoints: Record<string, string> = {
  leads: '/leads',
  hotels: '/hotels',
  'hotel-chains': '/hotel-chains',
  'job-titles': '/job-titles',
  companies: '/companies',
  'lead-sources': '/lead-sources',
  'contact-types': '/contact-types',
  tags: '/tags',
  interests: '/interests',
  'pms-systems': '/pms-systems',
  'lock-systems': '/lock-systems',
  'payment-processors': '/payment-processors',
  'hotel-categories': '/hotel-categories',
};

const getResourceUrl = (resource: string): string => {
  const endpoint = resourceEndpoints[resource];
  if (!endpoint) {
    console.warn(`Unknown resource: ${resource}, using default endpoint`);
    return `/${resource}`;
  }
  return endpoint;
};

/**
 * Map resource names to their ID field in the API
 * React Admin requires all records to have an 'id' field
 *
 * CUSTOMIZE: Add mappings if your API uses different ID field names
 */
const resourceIdFields: Record<string, string> = {
  // examples: 'id', // Default
  // If your API uses different ID fields:
  // products: 'productId',
};

const getIdField = (resource: string): string => {
  return resourceIdFields[resource] || 'id';
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Ensure record has an 'id' field for React Admin
 */
const ensureId = <T extends Record<string, any>>(
  record: T,
  resource: string
): T & { id: any } => {
  const idField = getIdField(resource);

  if (idField !== 'id' && idField in record) {
    return { ...record, id: record[idField] };
  }

  if ('id' in record && record.id !== undefined) {
    return record as T & { id: any };
  }

  console.warn(`Record missing identifier field '${idField}' for resource '${resource}'`);
  return record as T & { id: any };
};

const ensureIds = <T extends Record<string, any>>(
  records: T[],
  resource: string
): (T & { id: any })[] => {
  return records.map((record) => ensureId(record, resource));
};

/**
 * Build query string from React Admin params
 */
const buildQueryParams = (
  pagination?: ReactAdminPagination,
  sort?: ReactAdminSort,
  filter?: ReactAdminFilter
): string => {
  const params: Record<string, any> = {};

  if (pagination) {
    params.page = pagination.page;
    params.pageSize = pagination.perPage;
  }

  if (sort) {
    params.sortBy = sort.field;
    params.sortOrder = sort.order.toLowerCase();
  }

  if (filter) {
    Object.entries(filter).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          params[key] = value.join(',');
        } else {
          params[key] = value;
        }
      }
    });
  }

  return queryString.stringify(params);
};

/**
 * Check if response is paginated
 */
const isPagedResponse = (
  data: unknown
): data is PaginatedResponse<Record<string, any>> => {
  if (typeof data !== 'object' || data === null) return false;

  const response = data as Record<string, unknown>;
  return (
    Array.isArray(response.data) &&
    typeof response.totalCount === 'number' &&
    typeof response.page === 'number'
  );
};

/**
 * Transform paginated response to React Admin format
 */
const transformPagedResponse = <T extends Record<string, any>>(
  response: PaginatedResponse<T>,
  resource: string
): { data: (T & { id: any })[]; total: number } => {
  return {
    data: ensureIds(response.data, resource),
    total: response.totalCount,
  };
};

// ============================================
// DEMO DATA PROVIDER
// ============================================

/**
 * Demo data provider that works without a backend.
 * Handles any resource registered in demoDataRegistry.
 */
const demoDataProvider = {
  getList: async (resource, params) => {
    await new Promise((r) => setTimeout(r, 300));
    const getData = demoDataRegistry[resource];
    if (!getData) return { data: [], total: 0 };

    let data = [...getData()];

    // Apply filter
    if (params.filter) {
      if (params.filter.q) {
        const q = params.filter.q.toLowerCase();
        data = data.filter((item) =>
          Object.values(item).some(
            (val) => typeof val === 'string' && val.toLowerCase().includes(q)
          )
        );
      }
      Object.entries(params.filter).forEach(([key, value]) => {
        if (key !== 'q' && value !== undefined && value !== null && value !== '') {
          data = data.filter((item) => item[key] === value);
        }
      });
    }

    // Apply sort
    if (params.sort) {
      const { field, order } = params.sort;
      data.sort((a: any, b: any) => {
        if (a[field] < b[field]) return order === 'ASC' ? -1 : 1;
        if (a[field] > b[field]) return order === 'ASC' ? 1 : -1;
        return 0;
      });
    }

    // Apply pagination
    const { page, perPage } = params.pagination;
    const start = (page - 1) * perPage;
    const paginatedData = data.slice(start, start + perPage);

    return { data: paginatedData, total: data.length };
  },

  getOne: async (resource, params) => {
    await new Promise((r) => setTimeout(r, 200));
    const getData = demoDataRegistry[resource];
    if (getData) {
      const item = getData().find((e) => e.id === params.id);
      if (item) return { data: item };
    }
    throw new Error('Record not found');
  },

  getMany: async (resource, params) => {
    const getData = demoDataRegistry[resource];
    if (getData) {
      const data = getData().filter((e) => params.ids.includes(e.id));
      return { data };
    }
    return { data: [] };
  },

  getManyReference: async (resource, params) => {
    return { data: [], total: 0 };
  },

  create: async (resource, params) => {
    await new Promise((r) => setTimeout(r, 300));
    const getData = demoDataRegistry[resource];
    if (!getData) throw new Error('Resource not supported');

    const newItem = {
      id: String(Date.now()),
      ...params.data,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    setDemoData(resource, [newItem, ...getData()]);
    return { data: newItem };
  },

  update: async (resource, params) => {
    await new Promise((r) => setTimeout(r, 300));
    const getData = demoDataRegistry[resource];
    if (!getData) throw new Error('Record not found');

    const items = getData();
    const index = items.findIndex((e) => e.id === params.id);
    if (index !== -1) {
      items[index] = { ...items[index], ...params.data, updatedAt: new Date().toISOString() };
      setDemoData(resource, items);
      return { data: items[index] };
    }
    throw new Error('Record not found');
  },

  updateMany: async (resource, params) => {
    return { data: params.ids };
  },

  delete: async (resource, params) => {
    await new Promise((r) => setTimeout(r, 300));
    const getData = demoDataRegistry[resource];
    if (!getData) throw new Error('Record not found');

    const items = getData();
    const item = items.find((e) => e.id === params.id);
    if (item) {
      setDemoData(resource, items.filter((e) => e.id !== params.id));
      return { data: item };
    }
    throw new Error('Record not found');
  },

  deleteMany: async (resource, params) => {
    const getData = demoDataRegistry[resource];
    if (getData) {
      setDemoData(resource, getData().filter((e) => !params.ids.includes(e.id)));
    }
    return { data: params.ids };
  },
};

// ============================================
// REAL API DATA PROVIDER
// ============================================

const apiDataProvider: DataProvider = {
  /**
   * Get list of records
   */
  getList: async (resource, params) => {
    const { pagination, sort, filter } = params;
    const query = buildQueryParams(pagination, sort, filter);
    const url = `${getResourceUrl(resource)}?${query}`;

    const response = await httpClient.get<any>(url);
    const json = response.data;

    if (isPagedResponse(json)) {
      return transformPagedResponse(json, resource);
    }

    if (Array.isArray(json)) {
      return { data: ensureIds(json, resource), total: json.length };
    }

    if (json.data && Array.isArray(json.data)) {
      return { data: ensureIds(json.data, resource), total: json.data.length };
    }

    return { data: [], total: 0 };
  },

  /**
   * Get single record by ID
   */
  getOne: async (resource, params) => {
    const url = `${getResourceUrl(resource)}/${params.id}`;
    const response = await httpClient.get<any>(url);
    const json = response.data;

    if (json.data && !Array.isArray(json.data)) {
      return { data: ensureId(json.data, resource) };
    }

    return { data: ensureId(json, resource) };
  },

  /**
   * Get multiple records by IDs
   */
  getMany: async (resource, params) => {
    const query = queryString.stringify({ ids: params.ids.join(',') });
    const url = `${getResourceUrl(resource)}?${query}`;

    const response = await httpClient.get<any>(url);
    const json = response.data;

    if (isPagedResponse(json)) {
      return { data: ensureIds(json.data, resource) };
    }

    if (json.data && Array.isArray(json.data)) {
      return { data: ensureIds(json.data, resource) };
    }

    if (Array.isArray(json)) {
      return { data: ensureIds(json, resource) };
    }

    return { data: [] };
  },

  /**
   * Get records by reference (foreign key)
   */
  getManyReference: async (resource, params) => {
    const { pagination, sort, filter, target, id } = params;
    const query = buildQueryParams(pagination, sort, { ...filter, [target]: id });
    const url = `${getResourceUrl(resource)}?${query}`;

    const response = await httpClient.get<any>(url);
    const json = response.data;

    if (isPagedResponse(json)) {
      return transformPagedResponse(json, resource);
    }

    if (Array.isArray(json)) {
      return { data: ensureIds(json, resource), total: json.length };
    }

    return { data: [], total: 0 };
  },

  /**
   * Create new record
   */
  create: async (resource, params) => {
    const url = getResourceUrl(resource);
    const response = await httpClient.post<any>(url, params.data);
    const json = response.data;

    if (json.data) {
      return { data: ensureId(json.data, resource) };
    }

    return { data: ensureId({ ...params.data, ...json }, resource) };
  },

  /**
   * Update existing record
   */
  update: async (resource, params) => {
    const url = `${getResourceUrl(resource)}/${params.id}`;
    const response = await httpClient.put<any>(url, params.data);
    const json = response.data;

    if (json.data) {
      return { data: ensureId(json.data, resource) };
    }

    return { data: ensureId({ ...params.data, id: params.id }, resource) };
  },

  /**
   * Update multiple records
   */
  updateMany: async (resource, params) => {
    await Promise.all(
      params.ids.map((id) =>
        httpClient.put(`${getResourceUrl(resource)}/${id}`, params.data)
      )
    );

    return { data: params.ids };
  },

  /**
   * Delete single record
   */
  delete: async (resource, params) => {
    const url = `${getResourceUrl(resource)}/${params.id}`;
    await httpClient.delete(url);

    return { data: params.previousData as any };
  },

  /**
   * Delete multiple records
   */
  deleteMany: async (resource, params) => {
    await Promise.all(
      params.ids.map((id) => httpClient.delete(`${getResourceUrl(resource)}/${id}`))
    );

    return { data: params.ids };
  },
};

// ============================================
// CUSTOM METHODS
// ============================================

/**
 * Extended data provider interface with custom methods
 *
 * CUSTOMIZE: Add your custom API operations here
 */
export interface CustomDataProvider extends DataProvider {
  // Example custom method:
  // activateItem: (id: string) => Promise<{ data: any }>;
}

const customMethods = {
  // Example custom method:
  // activateItem: async (id: string): Promise<{ data: any }> => {
  //   const response = await httpClient.post(`/examples/${id}/activate`);
  //   return { data: response.data };
  // },
};

// ============================================
// EXPORT
// ============================================

/**
 * Select data provider based on demo mode
 */
/**
 * Layout-debug mock (dev server only, stripped from production builds):
 * serves generated leads and small catalogs without a backend.
 */
const mockLeads = Array.from({ length: 137 }, (_, i) => ({
  id: i + 1,
  treatment: i % 5 === 0 ? 'Sr.' : null,
  firstName: `Nombre${i + 1}`,
  lastName: `Apellido García ${i + 1}`,
  email: `lead${i + 1}@hotel-ejemplo.com`,
  secondaryEmail: null,
  mobilePhone: i % 3 === 0 ? `+34 600 00 ${String(i).padStart(2, '0')} 00` : null,
  city: ['Madrid', 'Santander', 'Lisboa', null][i % 4],
  region: null,
  country: ['España', 'Portugal', 'DEU', null][i % 4],
  status: ['Nuevo', 'Contactado', 'Interesado'][i % 3],
  preferredLanguage: 'ES',
  acceptsNewsletter: i % 2 === 0,
  notes: i % 6 === 0
    ? 'Observaciones largas de ejemplo para comprobar el truncado y el tooltip en la lista de contactos.'
    : null,
  companyName: i % 3 === 0 ? `Empresa ${i % 7}` : null,
  hotelName: i % 4 === 0 ? `Hotel Ejemplo ${i % 9}` : null,
  jobTitleName: i % 2 === 0 ? 'Director' : 'Recepción',
  contactTypeName: 'cliente',
  effectiveChainName: i % 5 === 0 ? 'Cadena Sol' : null,
  hotelCategoryName: null,
  hotelPmsSystemName: i % 4 === 0 ? 'PMS Demo' : null,
  hotelLockSystemName: null,
  hotelPaymentProcessorName: null,
  leadSourceName: 'Feria',
  tagNames: i % 3 === 0 ? ['VIP', 'con_presupuesto'] : [],
  interestNames: [],
  tagIds: [],
  interestIds: [],
  lastContactDate: i % 4 === 0 ? '2026-06-01' : null,
  nextContactDate: null,
  isArchived: false,
  createdAt: '2026-06-01T10:00:00Z',
  updatedAt: '2026-06-15T10:00:00Z',
}));

const mockCatalog = (names: string[]) =>
  names.map((name, index) => ({ id: index + 1, name, usageCount: index * 3 }));

const mockData: Record<string, any[]> = {
  leads: mockLeads,
  tags: mockCatalog(['VIP', 'con_presupuesto', 'feria-2026']),
  interests: mockCatalog(['Check-in online']),
  'job-titles': mockCatalog(['Director', 'Recepción']),
  companies: mockCatalog(['Empresa 1', 'Empresa 2']),
  'contact-types': mockCatalog(['cliente', 'proveedor']),
  'lead-sources': mockCatalog(['Feria', 'Web']),
  'hotel-categories': mockCatalog(['Urbano', 'Resort']),
  'pms-systems': mockCatalog(['PMS Demo']),
  'lock-systems': mockCatalog(['Cerradura Demo']),
  'payment-processors': mockCatalog(['TPV Demo']),
  'hotel-chains': mockCatalog(['Cadena Sol']).map(c => ({ ...c, hotelCount: 3 })),
  hotels: mockCatalog(['Hotel Ejemplo 1']).map(h => ({ ...h, leadCount: 5 })),
};

const mockDataProvider: DataProvider = {
  getList: async (resource, params) => {
    const all = mockData[resource] ?? [];
    const { page, perPage } = params.pagination ?? { page: 1, perPage: 25 };
    return { data: all.slice((page - 1) * perPage, page * perPage) as any, total: all.length };
  },
  getOne: async (resource, params) => ({
    data: (mockData[resource] ?? []).find(r => r.id === Number(params.id)) as any,
  }),
  getMany: async (resource, params) => ({
    data: (mockData[resource] ?? []).filter(r => params.ids.includes(r.id)) as any,
  }),
  getManyReference: async () => ({ data: [], total: 0 }),
  create: async (_r, params) => ({ data: { id: 999, ...params.data } as any }),
  update: async (_r, params) => ({ data: { id: params.id, ...params.data } as any }),
  updateMany: async (_r, params) => ({ data: params.ids }),
  delete: async (_r, params) => ({ data: params.previousData as any }),
  deleteMany: async (_r, params) => ({ data: params.ids }),
};

const useMock = import.meta.env.DEV && import.meta.env.VITE_MOCK === '1';

const baseDataProvider = useMock
  ? mockDataProvider
  : DEMO_MODE ? demoDataProvider as DataProvider : apiDataProvider;

export const dataProvider: CustomDataProvider = {
  ...baseDataProvider,
  ...customMethods,
};

export default dataProvider;
